import { forkJoin, of } from 'rxjs';
// Angular
import { Injectable } from '@angular/core';
// RxJS
import { map, mergeMap, catchError } from 'rxjs/operators';
// NGRX
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
// CRUD
import { QueryParamsModel } from '../../_base/crud';
// Services
import { FacilitiesService } from '../_services/facility.service';
// State
import { AppState } from '../../reducers';
// Actions
import {
  FacilityActionToggleLoading,
  FacilityActionTypes,
  FacilityCreated,
  FacilityOnServerCreated,
  FacilitiesPageLoaded,
  FacilitiesPageRequested,
  FacilitiesPageToggleLoading,
  FacilityStatusUpdated,
  FacilitiesStatusUpdated,
  FacilityUpdated,
  ManyFacilitiesDeleted,
  OneFacilityDeleted,
  ThrowFacilityError,
  ClearFacilityErrors,
  FacilitiesStatusOnServerUpdated,
  FacilityStatusOnServerUpdated,
  FacilityOnServerUpdated,
  ManyFacilitiesOnServerDeleted,
  OneFacilityOnServerDeleted,
  FacilitiesPageCancelled,
} from '../_actions/facility.actions';
import { Router } from '@angular/router';
import { AuthService } from '../../auth';
@Injectable()
export class FacilityEffects {
  showPageLoadingDistpatcher = new FacilitiesPageToggleLoading({ isLoading: true });
  showActionLoadingDistpatcher = new FacilityActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new FacilityActionToggleLoading({ isLoading: false });

  @Effect()
  loadFacilitiesPage$ = this.actions$.pipe(
    ofType<FacilitiesPageRequested>(FacilityActionTypes.FacilitiesPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.FacilitiesService.findFacilities(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new FacilitiesPageCancelled();
            } else {
              this.store.dispatch(new ThrowFacilityError({ error: response[0].error }));
              return new FacilitiesPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearFacilityErrors());
            return new FacilitiesPageLoaded({
              facilities: result.data,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteFacility$ = this.actions$.pipe(
    ofType<OneFacilityOnServerDeleted>(FacilityActionTypes.OneFacilityOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.FacilitiesService.deleteFacility(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowFacilityError({ error: res.error });
          } else {
            this.store.dispatch(new ClearFacilityErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new OneFacilityDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  deleteFacilities$ = this.actions$.pipe(
    ofType<ManyFacilitiesOnServerDeleted>(FacilityActionTypes.ManyFacilitiesOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.FacilitiesService.deleteFacilities(payload.ids).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowFacilityError({ error: res.error });
          } else {
            this.store.dispatch(new ClearFacilityErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new ManyFacilitiesDeleted(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateFacility$ = this.actions$.pipe(
    ofType<FacilityOnServerUpdated>(FacilityActionTypes.FacilityOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.FacilitiesService.updateFacility(payload.facility).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res && res.error) {
            return new ThrowFacilityError({ error: res.error });
          } else {
            this.store.dispatch(new ClearFacilityErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new FacilityUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateFacilityStatus$ = this.actions$.pipe(
    ofType<FacilityStatusOnServerUpdated>(FacilityActionTypes.FacilityStatusOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.FacilitiesService.updateStatusForFacility(payload.id, payload.is_active).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowFacilityError({ error: res.error });
          } else {
            this.store.dispatch(new ClearFacilityErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new FacilityStatusUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  updateFacilitiesStatus$ = this.actions$.pipe(
    ofType<FacilitiesStatusOnServerUpdated>(FacilityActionTypes.FacilitiesStatusOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.FacilitiesService.updateStatusForFacilities(payload.ids, payload.is_active).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowFacilityError({ error: res.error });
          } else {
            this.store.dispatch(new ClearFacilityErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new FacilitiesStatusUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createFacility$ = this.actions$.pipe(
    ofType<FacilityOnServerCreated>(FacilityActionTypes.FacilityOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.FacilitiesService.createFacility(payload.facility).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.errors) {
            return new ThrowFacilityError({ error: res });
          } else {
            this.store.dispatch(new ClearFacilityErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new FacilityCreated({ facility: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private FacilitiesService: FacilitiesService,
    private authService: AuthService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
